import styled from "styled-components";

import { Input as SharedInput } from "../IOSCompatibleInput/IOSCompatibleInput.style";
import { pxToRem } from "../../styles/utils";

export const BuilderSection = styled.section`
  margin-top: ${({ theme }) => pxToRem(theme.spacing.xl)};
  padding-top: ${({ theme }) => pxToRem(theme.spacing.xl)};
  border-top: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};
`;

export const BuilderHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => pxToRem(theme.spacing.lg)};
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.lg)};

  @media (max-width: 620px) {
    flex-direction: column;
  }
`;

export const BuilderTitle = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  font-weight: 600;
  line-height: 1.35;
`;

export const BuilderHint = styled.p`
  margin: ${({ theme }) => pxToRem(theme.spacing.xxs)} 0 0;
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  line-height: 1.45;
`;

export const QuestionsList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const QuestionBlock = styled.div<{ $dragging?: boolean }>`
  padding: ${({ theme }) => pxToRem(theme.spacing.lg)} 0;
  border-bottom: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};
  background: ${({ theme }) => theme.colors.white};
  opacity: ${({ $dragging }) => ($dragging ? 0.72 : 1)};
  position: relative;
  z-index: ${({ $dragging }) => ($dragging ? 2 : 1)};

  &:first-child {
    padding-top: 0;
  }
`;

export const QuestionTop = styled.div`
  display: grid;
  grid-template-columns: ${pxToRem(32)} minmax(${pxToRem(86)}, auto) minmax(${pxToRem(190)}, ${pxToRem(260)}) 1fr;
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.md)};

  @media (max-width: 700px) {
    grid-template-columns: ${pxToRem(32)} 1fr;

    > *:nth-child(3),
    > *:nth-child(4) {
      grid-column: 2;
    }
  }
`;

export const QuestionNumber = styled.span`
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 600;
  white-space: nowrap;
`;

export const DragHandle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${pxToRem(32)};
  height: ${pxToRem(32)};
  padding: 0;
  border: 0;
  border-radius: ${({ theme }) => pxToRem(theme.radii.sm)};
  background: transparent;
  color: ${({ theme }) => theme.colors.grayIcon};
  cursor: grab;

  svg {
    width: ${pxToRem(18)};
    height: ${pxToRem(18)};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.inkSurface};
    color: ${({ theme }) => theme.colors.ink};
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }

  &:active {
    cursor: grabbing;
  }
`;

export const QuestionActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
  flex-wrap: wrap;
`;

export const TextAction = styled.button<{ $danger?: boolean }>`
  padding: 0;
  border: 0;
  background: transparent;
  color: ${({ $danger, theme }) => ($danger ? theme.colors.dangerText : theme.colors.brand)};
  font: inherit;
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  cursor: pointer;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
    border-radius: ${({ theme }) => pxToRem(theme.radii.sm)};
  }

  &:disabled {
    color: ${({ theme }) => theme.colors.grayDisabled};
    cursor: default;
    text-decoration: none;
  }
`;

export const FieldsStack = styled.div`
  display: grid;
  gap: ${({ theme }) => pxToRem(theme.spacing.md)};
`;

export const FieldLabel = styled.label`
  display: grid;
  gap: ${({ theme }) => pxToRem(theme.spacing.xs)};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  font-weight: 500;
`;

export const TextArea = styled(SharedInput).attrs({ as: "textarea" })`
  box-sizing: border-box;
  min-height: ${pxToRem(76)};
  resize: vertical;
  background: ${({ theme }) => theme.colors.white};
  line-height: 1.45;
`;

export const OptionsSection = styled.div`
  margin-top: ${({ theme }) => pxToRem(theme.spacing.lg)};
`;

export const OptionsHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.sm)};
`;

export const OptionsTitle = styled.div`
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  font-weight: 500;
`;

export const OptionRow = styled.div<{ $dragging?: boolean }>`
  display: grid;
  grid-template-columns: ${pxToRem(32)} ${pxToRem(24)} minmax(0, 1fr) auto;
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.xs)};
  padding: ${({ theme }) => pxToRem(theme.spacing.xxs)} 0;
  opacity: ${({ $dragging }) => ($dragging ? 0.7 : 1)};

  @media (max-width: 560px) {
    grid-template-columns: ${pxToRem(32)} ${pxToRem(24)} minmax(0, 1fr);

    > *:last-child {
      grid-column: 3;
      justify-self: start;
    }
  }
`;

export const OptionInput = styled(SharedInput)`
  box-sizing: border-box;
  height: ${pxToRem(40)};
  padding-top: 0;
  padding-bottom: 0;
  background: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
`;

export const AttachmentArea = styled.div`
  display: grid;
  gap: ${({ theme }) => pxToRem(theme.spacing.xs)};
  margin-top: ${({ theme }) => pxToRem(theme.spacing.lg)};
`;

export const ExistingAttachment = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
  min-width: 0;
  padding: ${({ theme }) => pxToRem(theme.spacing.xs)} ${({ theme }) => pxToRem(theme.spacing.sm)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: ${({ theme }) => theme.colors.surfaceMuted};

  a {
    min-width: 0;
    overflow: hidden;
    color: ${({ theme }) => theme.colors.brand};
    font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const AddQuestionRow = styled.div`
  display: flex;
  justify-content: flex-start;
  padding-top: ${({ theme }) => pxToRem(theme.spacing.md)};
`;

export const ValidationMessage = styled.div`
  margin-top: ${({ theme }) => pxToRem(theme.spacing.sm)};
  color: ${({ theme }) => theme.colors.dangerText};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  line-height: 1.4;
`;
